import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import type { ConfigurableReceivingField } from '@lims/shared-types';
import { MAX_SAMPLES_PER_RECEIPT } from '@lims/shared-validation';
import {
  sampleReceiptsApi,
  sampleTypeSettingsApi,
  workOrdersApi,
  type ReceiveSamplesResult,
  type ReceivingContext,
  type SampleTypeDefinition,
} from '../../api/endpoints.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../app/auth-context.js';
import { useLaboratoryTimeZone } from '../../app/laboratory-time-zone.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  CheckboxGroupField,
  DateTimeField,
  FormPage,
  FormSection,
  LoadingState,
  MarkCaution,
  MarkInTolerance,
  NumberField,
  PageHeader,
  Panel,
  ReadOnlyField,
  SelectField,
  TextAreaField,
  TextField,
  newRowKey,
  patchRow,
  useToast,
  withoutRow,
  useLoadOnce,
} from '../../components/ui/index.js';
import { IconRemove } from '../../components/ui/nav-icons.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { WorkOrderContextCard } from './WorkOrderContextCard.js';
import { requestedTestName, requestedTestStandard } from './requested-tests.js';
import {
  AdditionalFieldsInputs,
  activeFields,
  additionalErrorText,
  additionalPayload,
  useSampleTypes,
  type AdditionalDraft,
} from './sample-type-fields.js';

/**
 * Receive the samples of one delivery (decisions D197, D288–D295).
 *
 * ===========================================================================
 * THIS PAGE RECORDS AN ARRIVAL. IT DOES NOT JUDGE THE MATERIAL.
 * ===========================================================================
 *
 * Every Sample ID is issued by the act of registering, BEFORE anybody has
 * decided whether the sample is acceptable (decision D72). There is no decision
 * control anywhere on this form: receiving material and judging it are separate
 * responsibilities, separate permissions and separate recorded acts.
 *
 * FIVE PARTS, IN THE ORDER A RECEIVER WORKS (D288):
 *  A. the work order, chosen once, shown as a compact band with its details
 *     on request (D194) — nothing on file is retyped;
 *  B. the delivery — arrival, deliverer, notes — given once for the receipt;
 *  C. the SHARED SAMPLE DETAILS — source, sampling time, sampler, condition,
 *     storage — typed once and used by every sample that keeps them (D290);
 *  D. one card per PHYSICAL sample, each becoming its own record with its own
 *     Sample ID; samples are never merged;
 *  E. a compact summary above the one Register action.
 *
 * SHARED IS A DEFAULT, NEVER A LINK THAT REWRITES (D290). A card that keeps the
 * shared details sends them as ITS OWN values; the server stores them on that
 * sample like any other. A card that is customised holds its own copy — seeded
 * from the shared details the first time — and nothing typed in the shared
 * section afterwards touches it. Going back to the shared details is an
 * explicit act, and the customised values stay on the card, so customising
 * again brings them back: no switch on this page discards what was typed.
 *
 * REQUESTED TESTS ARE THE WORK ORDER'S, AND ONLY THEIRS (D216, D293). Each card
 * lists the job's own services and nothing else — no catalogue, no search — and
 * chooses its own subset. Choosing one assigns nothing and creates no lab work:
 * that happens on acceptance (D233).
 *
 * WHAT IS ASKED FOR IS A DELIVERY, NOT A MEASUREMENT (D294). The description,
 * quantity and unit say what arrived. Anything more is an ADDITIONAL FIELD of
 * the chosen sample type, defined by the laboratory itself (0024, D296-D298)
 * and rendered from that definition: this page names no material. Concrete's
 * supplier, design strength, element and casting day are four such fields.
 * No reading, dimension, mass or result is asked for here (CLAUDE.md section 4).
 *
 * A TYPE'S DETAILS STAY WITH THAT TYPE (D298). Values are held per type on the
 * card, so switching the material and back restores them; only the final
 * type's own active fields are ever sent, and a hidden value never blocks.
 *
 * THE WHOLE DELIVERY IS ONE REQUEST AND ONE TRANSACTION: every sample is
 * registered, or none is and no number is spent. A refusal keeps every card,
 * every value and every choice on the page, and marks the field and the card
 * the server named.
 *
 * A ROW IS KEYED BY ITS OWN KEY, NEVER ITS POSITION (`rows.ts`). Removing the
 * second of three samples must not hand its values to the third. An empty
 * field is sent as absent, never as an empty string or a zero; who RECEIVED is
 * the signed-in person, recorded by the server.
 */

/** The sampling facts a delivery's samples usually share (D290). */
interface SamplingDetails {
  readonly sourceDescription: string;
  readonly sampledAt: string | null;
  readonly sampledBy: string;
  readonly conditionOnReceipt: string;
  readonly storageLocation: string;
}

type SamplingField = keyof SamplingDetails;

/** One physical sample of the delivery, as typed. */
interface SampleRow {
  readonly key: string;
  readonly sampleType: string | null;
  readonly description: string;
  readonly quantity: number | null;
  readonly quantityUnit: string;
  readonly clientReference: string;
  readonly notes: string;
  /** Whether this card sends the shared sampling details (D290). */
  readonly usesShared: boolean;
  /** Whether `own` was ever filled — it is seeded from the shared details once. */
  readonly ownSeeded: boolean;
  /** This card's own sampling details, used only while it is customised. */
  readonly own: SamplingDetails;
  /** Additional details typed, per sample type code, then by field id (0024). */
  readonly extra: Readonly<Record<string, AdditionalDraft>>;
  /**
   * Which of the work order's services THIS sample is for (D216). Each card
   * has its own subset; nothing assumes every service applies to every sample.
   */
  readonly requestedServiceIds: readonly string[];
}

type ReceivingService = ReceivingContext['services'][number];

const EMPTY_SAMPLING: SamplingDetails = {
  sourceDescription: '',
  sampledAt: null,
  sampledBy: '',
  conditionOnReceipt: '',
  storageLocation: '',
};

/** The sampling fields, in the order they are shown, with their configurable names (D70). */
const SAMPLING_FIELDS: readonly { readonly key: SamplingField; readonly config: ConfigurableReceivingField }[] = [
  { key: 'sourceDescription', config: 'source_description' },
  { key: 'sampledAt', config: 'sampled_at' },
  { key: 'sampledBy', config: 'sampled_by' },
  { key: 'conditionOnReceipt', config: 'condition_on_receipt' },
  { key: 'storageLocation', config: 'storage_location' },
];

function emptyRow(): SampleRow {
  return {
    key: newRowKey('sample'),
    sampleType: null,
    description: '',
    quantity: null,
    quantityUnit: '',
    clientReference: '',
    notes: '',
    usesShared: true,
    ownSeeded: false,
    own: EMPTY_SAMPLING,
    extra: {},
    requestedServiceIds: [],
  };
}

/**
 * Keep only the selections that still belong to the work order's services.
 * The same array when nothing changes, so a caller in an effect cannot loop.
 */
function keepSelectable(rows: SampleRow[], allowed: ReadonlySet<string>): SampleRow[] {
  let changed = false;
  const next = rows.map((row) => {
    const kept = row.requestedServiceIds.filter((id) => allowed.has(id));
    if (kept.length === row.requestedServiceIds.length) return row;
    changed = true;
    return { ...row, requestedServiceIds: kept };
  });
  return changed ? next : rows;
}

/** Trimmed, or null. An empty box is an unanswered question. */
function optional(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

const isBlank = (value: string | null) => value === null || value.trim() === '';

/** The sampling details a card sends: the shared ones, or its own (D290). */
function samplingOf(row: SampleRow, shared: SamplingDetails): SamplingDetails {
  return row.usesShared ? shared : row.own;
}

/** What one card sends: only what belongs to that sample. */
function lineBody(
  row: SampleRow,
  shared: SamplingDetails,
  hasWorkOrder: boolean,
  types: ReadonlyMap<string, SampleTypeDefinition>,
): Record<string, unknown> {
  const sampling = samplingOf(row, shared);
  const type = row.sampleType ? types.get(row.sampleType) : undefined;
  const additionalFields = additionalPayload(type, row.sampleType ? row.extra[row.sampleType] : undefined);
  return {
    // Without a job there is nothing to request (D216).
    ...(hasWorkOrder ? { requestedServiceIds: [...row.requestedServiceIds] } : {}),
    sampleType: row.sampleType,
    description: optional(row.description),
    quantity: row.quantity,
    quantityUnit: row.quantityUnit.trim(),
    clientReference: optional(row.clientReference),
    sourceDescription: optional(sampling.sourceDescription),
    sampledAt: sampling.sampledAt,
    sampledBy: optional(sampling.sampledBy),
    conditionOnReceipt: optional(sampling.conditionOnReceipt),
    storageLocation: optional(sampling.storageLocation),
    notes: optional(row.notes),
    // D298: only the chosen type's own active fields, only answered ones. What
    // was typed for another type stays on the card, unsent.
    ...(Object.keys(additionalFields).length > 0 ? { additionalFields } : {}),
  };
}

/**
 * Where a field the server names on a line lives on this page: on the card,
 * or in the sampling details (the card's own, or the shared ones it uses).
 * Validation names the request's own (camelCase) fields; a laboratory's rule
 * names its configured (snake_case) ones.
 */
type FieldPlace =
  | { readonly on: 'card'; readonly field: string; readonly label: string }
  | { readonly on: 'sampling'; readonly field: SamplingField; readonly label: string };

const LINE_FIELDS: Readonly<Record<string, FieldPlace>> = {
  sampleType: { on: 'card', field: 'sampleType', label: 'samples.sampleTypeLabel' },
  description: { on: 'card', field: 'description', label: 'receiving.sampleDescription' },
  quantity: { on: 'card', field: 'quantity', label: 'samples.quantity' },
  quantityUnit: { on: 'card', field: 'quantityUnit', label: 'samples.quantityUnit' },
  notes: { on: 'card', field: 'notes', label: 'receiving.sampleNotes' },
  clientReference: { on: 'card', field: 'clientReference', label: 'receivingField.client_reference' },
  client_reference: { on: 'card', field: 'clientReference', label: 'receivingField.client_reference' },
  requestedServiceIds: { on: 'card', field: 'requestedServiceIds', label: 'receiving.requestedTests' },
  ...Object.fromEntries(
    SAMPLING_FIELDS.flatMap(({ key, config }) => {
      const place: FieldPlace = { on: 'sampling', field: key, label: `receivingField.${config}` };
      return [
        [key, place],
        [config, place],
      ];
    }),
  ),
};

const LINE_FIELD = /^samples\.(\d+)\.(.+)$/;
const ADDITIONAL_FIELD = /^additionalFields\.(.+)$/;

/** What was sent, kept with the request so a refusal maps back to the cards it named. */
interface Submission {
  readonly rows: readonly SampleRow[];
  readonly shared: SamplingDetails;
}

export function SampleReceivePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const { can } = useAuth();
  const locale = currentLocale();
  const [searchParams] = useSearchParams();
  const receiptId = searchParams.get('receiptId');

  const timeZone = useLaboratoryTimeZone();
  // Every type and its fields in ONE cached request, whatever the card count.
  const sampleTypes = useSampleTypes();
  const [workOrderId, setWorkOrderId] = useState<string | null>(
    searchParams.get('workOrderId'),
  );
  const [receivedAt, setReceivedAt] = useState<string | null>(null);
  const [receivedFrom, setReceivedFrom] = useState('');
  const [receiptNotes, setReceiptNotes] = useState('');
  const [shared, setShared] = useState<SamplingDetails>(EMPTY_SAMPLING);
  const [rows, setRows] = useState<SampleRow[]>(() => [emptyRow()]);
  const [result, setResult] = useState<ReceiveSamplesResult | null>(null);
  // The card just added, so it can be brought into view once it exists.
  const addedKey = useRef<string | null>(null);

  // Reading the rules needs no extra permission: everybody who may receive a
  // sample needs to know what this laboratory insists on for its type.
  const typeSettings = useQuery({
    queryKey: ['sample-type-settings'],
    queryFn: () => sampleTypeSettingsApi.list(),
  });

  const workOrders = useQuery({
    queryKey: ['work-orders', 'selectable-for-sample'],
    queryFn: () => workOrdersApi.list({ pageSize: 100 }),
    enabled: receiptId === null,
  });

  /** Adding to an existing handover: its facts are fixed, not re-entered. */
  const receipt = useQuery({
    queryKey: ['sample-receipt', receiptId],
    queryFn: () => sampleReceiptsApi.get(receiptId as string),
    enabled: receiptId !== null,
  });

  useLoadOnce(receipt.data, (record) => {
    setWorkOrderId(record.workOrder?.id ?? null);
  });

  const context = useQuery({
    queryKey: ['receiving-context', workOrderId],
    queryFn: () => sampleReceiptsApi.workOrderContext(workOrderId as string),
    enabled: workOrderId !== null,
  });

  /**
   * The services a card may request: the CHOSEN work order's, not cancelled
   * (the server lists no cancelled one). Null while there is no job or its
   * context has not arrived — never the previous job's list.
   */
  const selectable =
    workOrderId !== null && context.data?.workOrder.id === workOrderId
      ? context.data.services
      : null;

  // Revalidate against the list the server sent: a selection of a service
  // that is no longer offered does not belong any more (D216).
  useEffect(() => {
    if (!selectable) return;
    const allowed = new Set(selectable.map((service) => service.id));
    setRows((current) => keepSelectable(current, allowed));
  }, [selectable]);

  /**
   * A different job: none of the cards' selections belong to it, since a
   * service belongs to exactly one work order. Everything else typed stays.
   */
  const changeWorkOrder = (next: string | null) => {
    if (next === workOrderId) return;
    setWorkOrderId(next);
    setRows((current) => keepSelectable(current, new Set()));
  };

  useEffect(() => {
    const key = addedKey.current;
    if (!key) return;
    addedKey.current = null;
    const card = document.querySelector<HTMLElement>(`[data-sample-row="${key}"]`);
    // Guarded: not every environment the page renders in can scroll.
    if (typeof card?.scrollIntoView === 'function') {
      card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [rows]);

  const requiredFor = (sampleType: string | null) =>
    new Set<string>(
      typeSettings.data?.find((entry) => entry.sampleType === sampleType)?.requiredFields ?? [],
    );

  const receive = useMutation({
    // The rows AS SUBMITTED travel with the request, so a problem the server
    // names by position ("samples.2.sampled_by") is traced to the card it came
    // from even if the list is edited afterwards.
    mutationFn: (submitted: Submission) => {
      const samples = submitted.rows.map((row) =>
        lineBody(row, submitted.shared, workOrderId !== null, sampleTypes.byCode),
      );
      return receiptId
        ? sampleReceiptsApi.addSamples(receiptId, { samples })
        : sampleReceiptsApi.receive({
            ...(workOrderId ? { workOrderId } : {}),
            // Already an instant, read on the LABORATORY's clock by the field.
            receivedAt: receivedAt ?? '',
            receivedFrom: receivedFrom.trim(),
            notes: optional(receiptNotes),
            samples,
          });
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['samples'] });
      await queryClient.invalidateQueries({ queryKey: ['sample-receipt'] });
      show({
        tone: 'success',
        message: t('receiving.resultTitle'),
        description: saved.receipt.receiptNumber,
      });
      setResult(saved);
    },
  });

  // ---------------------------------------------------------------------------
  // The server's problems, placed where they belong
  // ---------------------------------------------------------------------------

  /** Each card's problems as sentences, keyed by the card's own key. */
  const problemsByRow = new Map<string, string[]>();
  /** Each card's field errors, keyed by the card's own key, then the field. */
  const fieldErrorsByRow = new Map<string, Record<string, string>>();
  /** A shared field the server found missing, and the samples (by position) that use it. */
  const sharedProblems = new Map<SamplingField, { message: string; positions: number[] }>();
  /** Field name to "Sample N — Field", so the summary names the sample. */
  const fieldLabels: Record<string, string> = {
    workOrderId: t('samples.jobNumber'),
    receivedAt: t('samples.receivedAtLabel'),
    receivedFrom: t('samples.receivedFromLabel'),
    notes: t('receiving.receiptNotes'),
    samples: t('receiving.samplesSection'),
  };
  if (receive.error instanceof ApiError && receive.variables) {
    for (const detail of receive.error.details) {
      const match = detail.field ? LINE_FIELD.exec(detail.field) : null;
      if (!match || !detail.field) continue;
      const submittedRow = receive.variables.rows[Number(match[1])];
      // An additional field: named by its own label, marked on its own input.
      const additional = ADDITIONAL_FIELD.exec(match[2] as string);
      const additionalDef = additional
        ? sampleTypes.types.flatMap((type) => type.fields).find((field) => field.id === additional[1])
        : undefined;
      const place: FieldPlace | undefined = additional
        ? {
            on: 'card',
            field: `additional.${additional[1]}`,
            label: additionalDef ? (locale === 'ar' ? additionalDef.labelAr ?? additionalDef.labelEn : additionalDef.labelEn ?? additionalDef.labelAr) ?? 'receiving.additionalSection' : 'receiving.additionalSection',
          }
        : LINE_FIELDS[match[2] as string];
      if (!submittedRow || !place) continue;
      const position = rows.findIndex((row) => row.key === submittedRow.key);
      const field = t(place.label);
      if (position >= 0) {
        fieldLabels[detail.field] = t('receiving.fieldOfSample', { n: position + 1, field });
      }
      const message = additional
        ? additionalErrorText(t, detail.code ?? '')
        : t(`fieldErrors.${detail.code ?? ''}`, { defaultValue: '' }) || field;
      const fromShared = place.on === 'sampling' && submittedRow.usesShared;
      if (fromShared && place.on === 'sampling') {
        const entry = sharedProblems.get(place.field) ?? { message, positions: [] };
        if (position >= 0 && !entry.positions.includes(position + 1)) entry.positions.push(position + 1);
        sharedProblems.set(place.field, entry);
      } else {
        const errors = fieldErrorsByRow.get(submittedRow.key) ?? {};
        errors[place.field] = message;
        fieldErrorsByRow.set(submittedRow.key, errors);
      }
      const list = problemsByRow.get(submittedRow.key) ?? [];
      const sentence = fromShared
        ? t('receiving.problemInShared', { field, message })
        : message === field
          ? field
          : `${field}: ${message}`;
      if (!list.includes(sentence)) list.push(sentence);
      problemsByRow.set(submittedRow.key, list);
    }
  }

  // ---------------------------------------------------------------------------
  // Card actions
  // ---------------------------------------------------------------------------

  const updateRow = (key: string, change: Partial<Omit<SampleRow, 'key'>>) =>
    setRows((current) => patchRow(current, key, change));

  /** Customise: the card takes its own copy, seeded from the shared details the first time. */
  const customise = (row: SampleRow) =>
    updateRow(row.key, {
      usesShared: false,
      ownSeeded: true,
      own: row.ownSeeded ? row.own : shared,
    });

  /**
   * A copy of a card as a NEW, unsaved card right after it (D295): its material,
   * description, quantity, unit, sampling choice and values, additional details
   * and requested tests. Not its client reference or notes, which describe that
   * one sample — and never an identity: a Sample ID exists only once the server
   * issues it.
   */
  const duplicate = (row: SampleRow, position: number) => {
    const copy: SampleRow = { ...row, key: newRowKey('sample'), clientReference: '', notes: '' };
    addedKey.current = copy.key;
    setRows((current) => {
      const at = current.findIndex((entry) => entry.key === row.key);
      return [...current.slice(0, at + 1), copy, ...current.slice(at + 1)];
    });
    show({ tone: 'success', message: t('receiving.duplicatedSample', { n: position + 1 }) });
  };

  /**
   * Copy a card's material, description, unit and requested tests into the
   * OTHER cards' EMPTY fields (D295). A value somebody typed is never replaced,
   * so nothing on the page is lost by pressing it.
   */
  const copyToOthers = (source: SampleRow) => {
    let filled = 0;
    const next = rows.map((row) => {
      if (row.key === source.key) return row;
      const change: { -readonly [K in keyof SampleRow]?: SampleRow[K] } = {};
      if (row.sampleType === null && source.sampleType !== null) change.sampleType = source.sampleType;
      if (isBlank(row.description) && !isBlank(source.description)) change.description = source.description;
      if (isBlank(row.quantityUnit) && !isBlank(source.quantityUnit)) change.quantityUnit = source.quantityUnit;
      if (row.requestedServiceIds.length === 0 && source.requestedServiceIds.length > 0) {
        change.requestedServiceIds = [...source.requestedServiceIds];
      }
      if (Object.keys(change).length === 0) return row;
      filled += 1;
      return { ...row, ...change };
    });
    setRows(next);
    show({
      tone: filled > 0 ? 'success' : 'info',
      message: filled > 0 ? t('receiving.copiedToOthers', { count: filled }) : t('receiving.copiedToNone'),
    });
  };

  const startAgain = () => {
    setResult(null);
    setReceivedAt(null);
    setReceivedFrom('');
    setReceiptNotes('');
    setShared(EMPTY_SAMPLING);
    setRows([emptyRow()]);
    receive.reset();
  };

  if (receiptId !== null && receipt.isLoading) {
    return <LoadingState label={t('common.loading')} />;
  }
  const fromReceipt = receipt.data ?? null;

  if (result) {
    return <ReceiveResult result={result} onAgain={startAgain} />;
  }

  const sharingRows = rows.filter((row) => row.usesShared);
  /** A shared field is required when a sample that uses it has a type requiring it. */
  const sharedRequired = (config: ConfigurableReceivingField) =>
    sharingRows.some((row) => requiredFor(row.sampleType).has(config));
  const sharedError = (field: SamplingField) => {
    const problem = sharedProblems.get(field);
    return problem
      ? t('receiving.sharedFieldProblem', { message: problem.message, samples: problem.positions.join(t('samples.fieldSeparator')) })
      : undefined;
  };

  return (
    <FormPage
      title={t('samples.receiveTitle')}
      subtitle={t('samples.receiveSubtitle')}
      breadcrumbs={[
        { label: t('nav.samples'), to: '/samples' },
        { label: t('samples.receiveTitle') },
      ]}
      onSubmit={() => receive.mutate({ rows, shared })}
      onCancel={() => navigate('/samples')}
      isSubmitting={receive.isPending}
      canSubmit={can(PERMISSIONS.SAMPLES_RECEIVE)}
      submitLabel={t('samples.receiveSubmit')}
      error={receive.error}
      fieldLabels={fieldLabels}
      notices={
        <>
          {/*
            Registering an arrival is not a claim that the material is fit for
            testing. Said on the page, not only in a comment.
          */}
          <div className="mb-stack">
            <Alert
              tone="info"
              title={t('samples.receiveNoticeTitle')}
              description={t('samples.receiveNoticeBody')}
            />
          </div>
          {fromReceipt ? (
            <div className="mb-stack">
              <Alert
                tone="info"
                title={t('receiving.receiptBanner', { number: fromReceipt.receiptNumber })}
                description={t('receiving.receiptBannerHint')}
              />
            </div>
          ) : null}
        </>
      }
    >
      {/* A. The job, chosen once, and what is on file about it — read-only. */}
      <FormSection
        title={t('receiving.sectionContext')}
        description={t('samples.workOrderOptionalHint')}
        columns={1}
      >
        {fromReceipt ? (
          <ReadOnlyField label={t('samples.jobNumber')}>
            {fromReceipt.workOrder ? (
              <span dir="ltr">{fromReceipt.workOrder.workOrderNumber}</span>
            ) : (
              t('samples.noWorkOrder')
            )}
          </ReadOnlyField>
        ) : (
          /*
            Optional, deliberately. A sample can physically arrive before its
            paperwork, and refusing to record it until then would mean the
            laboratory holds material the system denies having.
          */
          <SelectField
            label={t('samples.jobNumber')}
            value={workOrderId}
            onChange={changeWorkOrder}
            placeholder={t('samples.noWorkOrder')}
            isLoading={workOrders.isLoading}
            options={(workOrders.data?.data ?? []).map((order) => ({
              value: order.id,
              label: order.workOrderNumber,
              description: order.clientName ?? t('common.notRecorded'),
            }))}
          />
        )}
        {workOrderId !== null ? (
          context.data ? (
            <WorkOrderContextCard context={context.data} />
          ) : context.isLoading ? (
            <LoadingState label={t('common.loading')} />
          ) : null
        ) : null}
      </FormSection>

      {/* B. The delivery, given once for the receipt. */}
      <FormSection
        title={t('samples.deliverySection')}
        description={t('samples.deliverySectionHint')}
        twoColumnsFrom="lg"
      >
        {fromReceipt ? (
          <>
            <ReadOnlyField label={t('samples.receivedAtLabel')}>
              {formatDateTime(fromReceipt.receivedAt, {
                locale,
                ...(timeZone ? { timezone: timeZone } : {}),
              })}
            </ReadOnlyField>
            <ReadOnlyField label={t('samples.receivedFromLabel')}>
              {fromReceipt.receivedFrom}
            </ReadOnlyField>
            {fromReceipt.notes ? (
              <ReadOnlyField label={t('receiving.receiptNotes')}>
                {fromReceipt.notes}
              </ReadOnlyField>
            ) : null}
          </>
        ) : (
          <>
            {/* A moment, not a day, read on the laboratory's clock (D192). */}
            <DateTimeField
              label={t('samples.receivedAtLabel')}
              description={t('samples.receivedAtHint')}
              value={receivedAt}
              onChange={setReceivedAt}
              timeZone={timeZone}
              isRequired
            />
            <TextField
              label={t('samples.receivedFromLabel')}
              description={t('samples.receivedFromHint')}
              value={receivedFrom}
              onChange={setReceivedFrom}
              isRequired
            />
            <div className="lg:col-span-2">
              <TextAreaField
                label={t('receiving.receiptNotes')}
                description={t('receiving.receiptNotesHint')}
                value={receiptNotes}
                onChange={setReceiptNotes}
                rows={2}
              />
            </div>
          </>
        )}
      </FormSection>

      {/* C. What most samples of one delivery share, typed once (D290). */}
      <FormSection
        title={t('receiving.sharedSection')}
        description={t('receiving.sharedSectionHint')}
        twoColumnsFrom="lg"
      >
        <SamplingFields
          values={shared}
          onChange={(change) => setShared((current) => ({ ...current, ...change }))}
          timeZone={timeZone}
          isRequired={sharedRequired}
          errorFor={sharedError}
          testId="shared-sample-details"
        />
        <div className="flex min-w-0 flex-wrap items-center gap-x-gutter gap-y-2 lg:col-span-2">
          <p className="text-caption text-default-600" aria-live="polite">
            {t('receiving.sharedUsage', { used: sharingRows.length, total: rows.length })}
          </p>
          {sharingRows.length < rows.length ? (
            <ActionButton
              priority="secondary"
              size="sm"
              onPress={() =>
                setRows((current) => current.map((row) => (row.usesShared ? row : { ...row, usesShared: true })))
              }
            >
              {t('receiving.applySharedToAll')}
            </ActionButton>
          ) : null}
        </div>
      </FormSection>

      {/* D. One card per physical sample. */}
      <FormSection
        title={t('receiving.samplesSection')}
        description={t('receiving.samplesSectionHint')}
        columns={1}
      >
        {rows.map((row, index) => (
          <SampleCard
            key={row.key}
            row={row}
            position={index + 1}
            shared={shared}
            types={sampleTypes.types}
            isLoadingTypes={sampleTypes.isLoading}
            typeName={sampleTypes.nameOf}
            required={requiredFor(row.sampleType)}
            problems={problemsByRow.get(row.key) ?? []}
            fieldErrors={fieldErrorsByRow.get(row.key) ?? {}}
            services={workOrderId === null ? null : selectable}
            isLoadingServices={workOrderId !== null && selectable === null && context.isLoading}
            timeZone={timeZone}
            canCopy={rows.length > 1}
            canAdd={rows.length < MAX_SAMPLES_PER_RECEIPT}
            onChange={(change) => updateRow(row.key, change)}
            onCustomise={() => customise(row)}
            onUseShared={() => updateRow(row.key, { usesShared: true })}
            onDuplicate={() => duplicate(row, index)}
            onCopyToOthers={() => copyToOthers(row)}
            onRemove={() => setRows((current) => withoutRow(current, row.key))}
          />
        ))}
        {rows.length === 0 ? (
          <p className="text-body text-default-600">{t('receiving.noSamplesYet')}</p>
        ) : null}
        <div className="flex flex-wrap items-center gap-stack">
          <ActionButton
            priority="secondary"
            size="sm"
            isDisabled={rows.length >= MAX_SAMPLES_PER_RECEIPT}
            onPress={() => {
              const row = emptyRow();
              addedKey.current = row.key;
              setRows((current) => [...current, row]);
            }}
          >
            {t('receiving.addSample')}
          </ActionButton>
          {rows.length >= MAX_SAMPLES_PER_RECEIPT ? (
            <span className="text-caption text-default-600">
              {t('receiving.maxSamples', { max: MAX_SAMPLES_PER_RECEIPT })}
            </span>
          ) : null}
        </div>
      </FormSection>

      {/* E. What the one Register action will record. Not a step: just a look. */}
      {rows.length > 0 ? (
        <ReceiveSummary
          rows={rows}
          services={selectable}
          receivedAt={fromReceipt ? fromReceipt.receivedAt : receivedAt}
          receivedFrom={fromReceipt ? fromReceipt.receivedFrom : receivedFrom}
          timeZone={timeZone}
          typeName={sampleTypes.nameOf}
        />
      ) : null}
    </FormPage>
  );
}

/**
 * The five sampling fields, used twice: once for the shared details and once
 * on a customised card. The same labels and the same rules in both places.
 */
function SamplingFields({
  values,
  onChange,
  timeZone,
  isRequired,
  errorFor,
  testId,
}: {
  readonly values: SamplingDetails;
  readonly onChange: (change: Partial<SamplingDetails>) => void;
  readonly timeZone: string | null;
  readonly isRequired: (field: ConfigurableReceivingField) => boolean;
  readonly errorFor: (field: SamplingField) => string | undefined;
  readonly testId: string;
}) {
  const { t } = useTranslation();
  const error = (field: SamplingField) => {
    const message = errorFor(field);
    return message ? { error: message } : {};
  };
  return (
    <>
      <div className="min-w-0" data-testid={testId}>
        <TextField
          label={t('receivingField.source_description')}
          value={values.sourceDescription}
          onChange={(sourceDescription) => onChange({ sourceDescription })}
          isRequired={isRequired('source_description')}
          {...error('sourceDescription')}
        />
      </div>
      <DateTimeField
        label={t('receivingField.sampled_at')}
        description={t('samples.sampledAtHint')}
        value={values.sampledAt}
        onChange={(sampledAt) => onChange({ sampledAt })}
        timeZone={timeZone}
        isRequired={isRequired('sampled_at')}
        {...error('sampledAt')}
      />
      <TextField
        label={t('receivingField.sampled_by')}
        value={values.sampledBy}
        onChange={(sampledBy) => onChange({ sampledBy })}
        isRequired={isRequired('sampled_by')}
        {...error('sampledBy')}
      />
      <TextField
        label={t('receivingField.storage_location')}
        description={t('samples.storageLocationHint')}
        value={values.storageLocation}
        onChange={(storageLocation) => onChange({ storageLocation })}
        isRequired={isRequired('storage_location')}
        {...error('storageLocation')}
      />
      <div className="min-w-0 lg:col-span-2">
        {/*
          Free text, as stored. No list of condition codes exists in the
          laboratory's files, and inventing one would put words in the
          receiver's mouth. Condition is an observation, not a decision.
        */}
        <TextAreaField
          label={t('receivingField.condition_on_receipt')}
          description={t('samples.conditionHint')}
          value={values.conditionOnReceipt}
          onChange={(conditionOnReceipt) => onChange({ conditionOnReceipt })}
          isRequired={isRequired('condition_on_receipt')}
          rows={1}
          {...error('conditionOnReceipt')}
        />
      </div>
    </>
  );
}

/**
 * One physical sample: only what belongs to it. A fieldset named "Sample N",
 * so a screen reader hears which sample a field belongs to.
 */
function SampleCard({
  row,
  position,
  shared,
  types,
  isLoadingTypes,
  typeName,
  required,
  problems,
  fieldErrors,
  services,
  isLoadingServices,
  timeZone,
  canCopy,
  canAdd,
  onChange,
  onCustomise,
  onUseShared,
  onDuplicate,
  onCopyToOthers,
  onRemove,
}: {
  readonly row: SampleRow;
  readonly position: number;
  readonly shared: SamplingDetails;
  /** This laboratory's types, archived ones included (they are filtered here). */
  readonly types: readonly SampleTypeDefinition[];
  readonly isLoadingTypes: boolean;
  readonly typeName: (code: string) => string;
  readonly required: ReadonlySet<string>;
  readonly problems: readonly string[];
  /** The server's problems with this card's own fields, translated, by field. */
  readonly fieldErrors: Readonly<Record<string, string>>;
  /** The chosen job's services; null without a job (nothing to request). */
  readonly services: readonly ReceivingService[] | null;
  readonly isLoadingServices: boolean;
  readonly timeZone: string | null;
  readonly canCopy: boolean;
  readonly canAdd: boolean;
  readonly onChange: (change: Partial<Omit<SampleRow, 'key'>>) => void;
  readonly onCustomise: () => void;
  readonly onUseShared: () => void;
  readonly onDuplicate: () => void;
  readonly onCopyToOthers: () => void;
  readonly onRemove: () => void;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const samplingId = useId();
  const isRequired = (field: ConfigurableReceivingField) => required.has(field);
  const title = t('receiving.sampleCard', { n: position });
  const error = (field: string) => (fieldErrors[field] ? { error: fieldErrors[field] } : {});
  const additionalId = useId();
  const type = types.find((entry) => entry.code === row.sampleType);
  const fields = activeFields(type);
  const draft = (row.sampleType ? row.extra[row.sampleType] : undefined) ?? {};
  // Values typed for ANOTHER type stay on the card, unsent, and are said to.
  const keptOther = Object.entries(row.extra).some(
    ([code, values]) => code !== row.sampleType && Object.values(values).some((value) => value.trim() !== ''),
  );
  const additionalErrors = Object.fromEntries(
    Object.entries(fieldErrors)
      .filter(([key]) => key.startsWith('additional.'))
      .map(([key, message]) => [key.slice('additional.'.length), message]),
  );
  const material = row.sampleType ? typeName(row.sampleType) : null;
  const heading = [material, optional(row.description)].filter(Boolean).join(' · ');
  // Required by this laboratory for this material, and missing from the shared details it uses.
  const missingShared = row.usesShared
    ? SAMPLING_FIELDS.filter(({ key, config }) => required.has(config) && isBlank(shared[key] ?? null)).map(
        ({ config }) => t(`receivingField.${config}`),
      )
    : [];

  return (
    <fieldset
      className="min-w-0 rounded-medium border border-default-200 p-3 sm:p-4"
      data-sample-row={row.key}
      data-testid="sample-row"
    >
      <legend className="px-1 text-body font-semibold text-default-900">{title}</legend>
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-gutter gap-y-2">
        <p className="min-w-0 break-words text-body text-default-700" data-testid="sample-heading">
          {heading || t('receiving.materialNotChosen')}
        </p>
        <div className="flex flex-wrap items-center gap-1">
          <ActionButton
            priority="tertiary"
            size="sm"
            isDisabled={!canAdd}
            aria-label={t('receiving.duplicateSampleLabel', { n: position })}
            onPress={onDuplicate}
          >
            {t('receiving.duplicateSample')}
          </ActionButton>
          {canCopy ? (
            <ActionButton
              priority="tertiary"
              size="sm"
              aria-label={t('receiving.copyToOthersLabel', { n: position })}
              onPress={onCopyToOthers}
            >
              {t('receiving.copyToOthers')}
            </ActionButton>
          ) : null}
          {/* Nothing is saved until Register, so a card goes at once, unconfirmed. */}
          <ActionButton
            priority="destructive"
            size="sm"
            startContent={<IconRemove size={16} />}
            aria-label={t('receiving.removeSampleLabel', { n: position })}
            onPress={onRemove}
          >
            {t('receiving.removeSample')}
          </ActionButton>
        </div>
      </div>
      {problems.length > 0 ? (
        <div className="mt-stack">
          <Alert
            tone="warning"
            title={t('receiving.rowProblems', { n: position })}
            description={problems.join(t('samples.fieldSeparator'))}
          />
        </div>
      ) : null}

      {/* What the sample is, and how much of it arrived. */}
      <div className="mt-stack grid min-w-0 grid-cols-1 gap-x-gutter gap-y-stack lg:grid-cols-2">
        <SelectField
          label={t('samples.sampleTypeLabel')}
          value={row.sampleType}
          onChange={(sampleType) => onChange({ sampleType })}
          isRequired
          placeholder={t('samples.sampleTypePlaceholder')}
          isLoading={isLoadingTypes}
          // Archived types leave new receiving; the laboratory's own order and names.
          options={types
            .filter((entry) => !entry.archived || entry.code === row.sampleType)
            .map((entry) => ({ value: entry.code, label: typeName(entry.code) }))}
          {...error('sampleType')}
        />
        <TextField
          label={t('receiving.sampleDescription')}
          description={t('receiving.sampleDescriptionHint')}
          value={row.description}
          onChange={(description) => onChange({ description })}
          {...error('description')}
        />
        <div className="grid min-w-0 grid-cols-2 gap-x-gutter">
          <NumberField
            label={t('samples.quantity')}
            value={row.quantity}
            onChange={(quantity) => onChange({ quantity })}
            isRequired
            step="any"
            {...error('quantity')}
          />
          <TextField
            label={t('samples.quantityUnit')}
            value={row.quantityUnit}
            onChange={(quantityUnit) => onChange({ quantityUnit })}
            isRequired
            placeholder={t('samples.quantityUnitPlaceholder')}
            {...error('quantityUnit')}
          />
        </div>
        <TextField
          label={t('receivingField.client_reference')}
          description={t('receiving.sampleReferenceHint')}
          value={row.clientReference}
          onChange={(clientReference) => onChange({ clientReference })}
          isRequired={isRequired('client_reference')}
          {...error('clientReference')}
        />
      </div>

      {/* Sampling: the shared details, or this sample's own (D290). */}
      <section
        aria-labelledby={samplingId}
        className="mt-stack border-t border-default-200 pt-stack"
        data-testid="sample-sampling"
      >
        <h4 id={samplingId} className="text-body font-semibold text-default-900">
          {t('receiving.samplingSection')}
        </h4>
        {row.usesShared ? (
          <div className="mt-2 flex min-w-0 flex-wrap items-start justify-between gap-x-gutter gap-y-2">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-body text-default-900">
                <MarkInTolerance />
                {t('receiving.usesShared')}
              </p>
              <p className="mt-0.5 break-words text-caption text-default-600">
                {sharedSummary(shared, t, locale, timeZone) || t('receiving.sharedEmpty')}
              </p>
              {missingShared.length > 0 ? (
                <p className="mt-1 flex items-start gap-1.5 text-caption text-warning-800">
                  <MarkCaution />
                  <span>{t('receiving.sharedMissing', { fields: missingShared.join(t('samples.fieldSeparator')) })}</span>
                </p>
              ) : null}
            </div>
            <ActionButton
              priority="secondary"
              size="sm"
              aria-label={t('receiving.customiseSampleLabel', { n: position })}
              onPress={onCustomise}
            >
              {t('receiving.customiseSample')}
            </ActionButton>
          </div>
        ) : (
          <>
            <div className="mt-2 flex min-w-0 flex-wrap items-center justify-between gap-x-gutter gap-y-2">
              <p className="min-w-0 text-caption text-default-600">{t('receiving.customisedNote')}</p>
              <ActionButton
                priority="tertiary"
                size="sm"
                aria-label={t('receiving.useSharedAgainLabel', { n: position })}
                onPress={onUseShared}
              >
                {t('receiving.useSharedAgain')}
              </ActionButton>
            </div>
            <div className="mt-stack grid min-w-0 grid-cols-1 gap-x-gutter gap-y-stack lg:grid-cols-2">
              <SamplingFields
                values={row.own}
                onChange={(change) => onChange({ own: { ...row.own, ...change } })}
                timeZone={timeZone}
                isRequired={isRequired}
                errorFor={(field) => fieldErrors[field]}
                testId="own-sample-details"
              />
            </div>
          </>
        )}
      </section>

      {/*
        0024: the chosen type's own additional fields, rendered from its
        definition. Never a reading: what the work produces is recorded when
        the work is done, not at the counter.
      */}
      {fields.length > 0 ? (
        <section
          className="mt-stack border-t border-default-200 pt-stack"
          aria-labelledby={additionalId}
          data-testid="additional-sample-details"
        >
          <h4 id={additionalId} className="text-body font-semibold text-default-900">
            {t('receiving.additionalSection')}
          </h4>
          <p className="mt-0.5 text-caption text-default-600">{t('receiving.additionalSectionHint')}</p>
          <div className="mt-stack min-w-0">
            <AdditionalFieldsInputs
              fields={fields}
              values={draft}
              errors={additionalErrors}
              onChange={(fieldId, value) =>
                row.sampleType &&
                onChange({ extra: { ...row.extra, [row.sampleType]: { ...draft, [fieldId]: value } } })
              }
            />
          </div>
        </section>
      ) : null}
      {keptOther ? (
        <p className="mt-stack text-caption text-default-600">{t('receiving.additionalKept')}</p>
      ) : null}

      {/*
        D216, D293: which of the JOB's services this sample is for — the work
        order's own list, nothing from the catalogue, no search. Each card
        chooses its own. Choosing a test assigns nothing and performs nothing;
        acceptance and assignment come later.
      */}
      {services !== null ? (
        <div className="mt-stack border-t border-default-200 pt-stack" data-testid="requested-tests">
          {services.length === 0 ? (
            <ReadOnlyField label={t('receiving.requestedTestsForSample')}>
              {t('receiving.requestedTestsNone')}
            </ReadOnlyField>
          ) : (
            <>
              <CheckboxGroupField
                label={t('receiving.requestedTestsForSample')}
                description={t('receiving.requestedTestsHint')}
                value={row.requestedServiceIds}
                onChange={(requestedServiceIds) => onChange({ requestedServiceIds })}
                isRequired
                {...(fieldErrors.requestedServiceIds ? { error: fieldErrors.requestedServiceIds } : {})}
                options={services.map((service) => ({
                  value: service.id,
                  label: checklistLabel(service, locale, t('common.notRecorded')),
                }))}
              />
              <p className="mt-1 text-caption text-default-600" aria-live="polite">
                {t('receiving.requestedTestsCount', {
                  selected: row.requestedServiceIds.length,
                  total: services.length,
                })}
              </p>
            </>
          )}
        </div>
      ) : isLoadingServices ? (
        <div className="mt-stack">
          <LoadingState label={t('common.loading')} />
        </div>
      ) : null}

      <div className="mt-stack border-t border-default-200 pt-stack">
        <TextAreaField
          label={t('receiving.sampleNotes')}
          value={row.notes}
          onChange={(notes) => onChange({ notes })}
          rows={1}
          {...error('notes')}
        />
      </div>
    </fieldset>
  );
}

/**
 * "ASTM C136 — Sieve analysis": the standard first, as the laboratory names its
 * tests, then the work order's own name for the service. The standard is a
 * code and reads left to right inside an Arabic line without turning.
 */
function checklistLabel(service: ReceivingService, locale: string, fallback: string): string {
  const name = requestedTestName(service.description.ar, service.description.en, locale) ?? fallback;
  const standard = requestedTestStandard(service.referenceStandard, service.standardVersion);
  // U+2066/U+2069 isolate the code, so its digits and letters keep their order.
  return standard ? `⁦${standard}⁩ — ${name}` : name;
}

/** The shared details in one line, as a card that uses them shows them. */
function sharedSummary(
  shared: SamplingDetails,
  t: (key: string, options?: Record<string, unknown>) => string,
  locale: ReturnType<typeof currentLocale>,
  timeZone: string | null,
): string {
  const sampled = shared.sampledAt
    ? formatDateTime(shared.sampledAt, { locale, ...(timeZone ? { timezone: timeZone } : {}) })
    : null;
  return [
    optional(shared.sourceDescription),
    sampled,
    optional(shared.sampledBy) ? t('receiving.sampledByShort', { name: shared.sampledBy.trim() }) : null,
    optional(shared.conditionOnReceipt),
    optional(shared.storageLocation),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** E. A look at what Register will record. Every value is the form's own. */
function ReceiveSummary({
  rows,
  services,
  receivedAt,
  receivedFrom,
  timeZone,
  typeName,
}: {
  readonly rows: readonly SampleRow[];
  readonly services: readonly ReceivingService[] | null;
  readonly receivedAt: string | null;
  readonly receivedFrom: string;
  readonly timeZone: string | null;
  readonly typeName: (code: string) => string;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const notEntered = t('receiving.summaryNotEntered');
  const byId = new Map((services ?? []).map((service) => [service.id, service]));
  return (
    <FormSection title={t('receiving.summaryTitle')} columns={1}>
      <div className="min-w-0" data-testid="receive-summary">
        <dl className="grid grid-cols-1 gap-x-gutter gap-y-2 sm:grid-cols-3">
          <div className="min-w-0">
            <dt className="text-caption text-default-600">{t('receiving.summarySamples')}</dt>
            <dd className="text-body text-default-900">{rows.length}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-caption text-default-600">{t('samples.receivedAtLabel')}</dt>
            <dd className="text-body text-default-900">
              {receivedAt
                ? formatDateTime(receivedAt, { locale, ...(timeZone ? { timezone: timeZone } : {}) })
                : notEntered}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-caption text-default-600">{t('samples.receivedFromLabel')}</dt>
            <dd className="break-words text-body text-default-900">{optional(receivedFrom) ?? notEntered}</dd>
          </div>
        </dl>
        <ol className="mt-stack flex flex-col gap-1">
          {rows.map((row, index) => {
            const tests = row.requestedServiceIds
              .map((id) => byId.get(id))
              .filter((service): service is ReceivingService => service !== undefined)
              .map(
                (service) =>
                  requestedTestStandard(service.referenceStandard, service.standardVersion) ??
                  requestedTestName(service.description.ar, service.description.en, locale) ??
                  t('common.notRecorded'),
              );
            return (
              <li key={row.key} className="min-w-0 break-words text-body text-default-900">
                <span className="font-semibold">{t('receiving.sampleCard', { n: index + 1 })}</span>
                {' — '}
                {[
                  row.sampleType ? typeName(row.sampleType) : t('receiving.materialNotChosen'),
                  optional(row.description),
                  row.quantity !== null && optional(row.quantityUnit)
                    ? `${row.quantity} ${row.quantityUnit.trim()}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                {services !== null ? (
                  <span className="block text-caption text-default-600">
                    {tests.length > 0 ? (
                      <bdi dir="ltr">{tests.join(', ')}</bdi>
                    ) : (
                      t('receiving.summaryNoTests')
                    )}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
      </div>
    </FormSection>
  );
}

/**
 * What the server confirmed: the receipt, the job, and every Sample ID it
 * issued — read from the response, never assumed from the form (CLAUDE.md 8).
 */
function ReceiveResult({
  result,
  onAgain,
}: {
  readonly result: ReceiveSamplesResult;
  readonly onAgain: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { receipt, received } = result;
  const job = receipt.workOrder;
  const first = received[0];
  // The outcome takes focus: it is scrolled to, and read out, at once.
  const outcome = useRef<HTMLDivElement>(null);
  useEffect(() => outcome.current?.focus(), []);
  const samplesLink = job
    ? `/samples?workOrderId=${job.id}&job=${encodeURIComponent(job.workOrderNumber)}`
    : '/samples';
  return (
    <>
      <PageHeader
        title={t('samples.receiveTitle')}
        breadcrumbs={[
          { label: t('nav.samples'), to: '/samples' },
          { label: t('samples.receiveTitle') },
        ]}
      />
      <div className="flex w-full max-w-4xl flex-col gap-gutter" data-testid="receive-result">
        <div ref={outcome} tabIndex={-1} className="outline-none">
          <Alert
            tone="success"
            title={t('receiving.resultTitle')}
            description={[
              t('receiving.resultReceipt', { number: receipt.receiptNumber }),
              job ? t('receiving.resultJob', { number: job.workOrderNumber }) : t('samples.noWorkOrder'),
              t('receiving.resultSamples', { count: received.length }),
            ].join(' — ')}
          />
        </div>
        <Panel title={t('receiving.samplesOnReceipt')}>
          <ul className="flex flex-wrap gap-2" aria-label={t('receiving.samplesOnReceipt')}>
            {received.map((sample) => (
              <li key={sample.id}>
                <Link
                  to={`/samples/${sample.id}`}
                  className="measure inline-block rounded-medium border border-default-200 bg-white px-2 py-1 text-body font-semibold text-default-900 hover:border-primary-600 hover:text-primary-700"
                  dir="ltr"
                >
                  {sample.sampleNumber}
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-stack text-caption text-default-600">
            {t('receiving.resultNotAcceptance')}
          </p>
          <div className="mt-stack">
            <ActionBar>
              <ActionButton priority="tertiary" onPress={onAgain}>
                {t('receiving.receiveAnother')}
              </ActionButton>
              <ActionButton priority="secondary" onPress={() => navigate(samplesLink)}>
                {t('receiving.viewSamples')}
              </ActionButton>
              {first && can(PERMISSIONS.SAMPLES_DECIDE) ? (
                <ActionButton priority="secondary" onPress={() => navigate(`/samples/${first.id}`)}>
                  {t('receiving.proceedToAcceptance')}
                </ActionButton>
              ) : null}
              <ActionButton
                priority="primary"
                onPress={() => navigate(`/samples/receipts/${receipt.id}`)}
              >
                {t('receiving.viewReceipt')}
              </ActionButton>
            </ActionBar>
          </div>
        </Panel>
      </div>
    </>
  );
}
